import React, { useState } from 'react';
import SettingsLayout from './SettingsLayout';
import {
  ChevronDown, MessageCircle, Mail, Book, LifeBuoy,
} from 'lucide-react';

const FAQS = [
  {
    q: 'How do I book a service?',
    a: "Head to Discover, tap a service, pick a date and time, and add it to your bag. You'll see the pro's payment policy (pay after, deposit, or full upfront) before confirming.",
  },
  {
    q: 'When will my booking be confirmed?',
    a: "The pro receives your request instantly and usually confirms within a few hours. You'll get a notification the moment they respond.",
  },
  {
    q: 'What payment methods can I use?',
    a: 'Depending on the pro — Card (via Paystack), PayShap, or Cash at the appointment. Every pro lists which methods they accept.',
  },
  {
    q: 'How do I cancel a booking?',
    a: 'Open the booking in your Calendar and tap Cancel. If you paid a deposit, the pro will follow up about a refund based on their cancellation policy.',
  },
  {
    q: 'Is my payment secure?',
    a: 'Yes. Card payments are handled end-to-end by Paystack — we never see or store your card details.',
  },
  {
    q: 'How do I share my cart or ask someone to pay?',
    a: "Open your bag and tap Share cart, or tap Ask someone to pay at checkout. You'll get a link you can send to anyone — they don't need a Skytone account.",
  },
  {
    q: 'Can I sell on Skytone?',
    a: "Yes! Tap Skytone Pro on your profile to switch to a professional account and set up your services and products.",
  },
];

const HelpCenterPage = () => {
  const [openIdx, setOpenIdx] = useState(null);

  const contactSubject = encodeURIComponent('Skytone support request');
  const contactBody = encodeURIComponent('Hi Skytone team,\n\nI need help with...\n\n');

  return (
    <SettingsLayout
      title="Help Center"
      subtitle="Answers to common questions"
    >
      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-3">
        <a
          href={`mailto:info@maconnect.co.za?subject=${contactSubject}&body=${contactBody}`}
          className="bg-white rounded-2xl border border-gray-100 p-4 text-left hover:border-[#EFA9B6] transition-colors"
        >
          <div className="w-10 h-10 rounded-xl bg-[#FFF5F6] flex items-center justify-center text-[#EFA9B6] mb-3">
            <Mail size={18} />
          </div>
          <p className="font-bold text-gray-900 text-sm">Email support</p>
          <p className="text-xs text-gray-500 mt-0.5">support@skytone.app</p>
        </a>

        <button
          onClick={() => { window.location.href = '/dashboard/chat'; }}
          className="bg-white rounded-2xl border border-gray-100 p-4 text-left hover:border-[#EFA9B6] transition-colors"
        >
          <div className="w-10 h-10 rounded-xl bg-[#FFF5F6] flex items-center justify-center text-[#EFA9B6] mb-3">
            <MessageCircle size={18} />
          </div>
          <p className="font-bold text-gray-900 text-sm">Message a pro</p>
          <p className="text-xs text-gray-500 mt-0.5">If it's about a booking</p>
        </button>
      </div>

      {/* FAQ */}
      <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-50">
          <div className="flex items-center gap-2">
            <Book size={16} className="text-[#EFA9B6]" />
            <h2 className="font-serif font-bold text-gray-900">Frequently asked</h2>
          </div>
        </div>

        {FAQS.map((faq, idx) => {
          const open = openIdx === idx;
          return (
            <div
              key={idx}
              className={idx < FAQS.length - 1 ? 'border-b border-gray-50' : ''}
            >
              <button
                onClick={() => setOpenIdx(open ? null : idx)}
                className="w-full p-4 flex items-start gap-3 text-left hover:bg-[#FCF8F9] transition-colors"
              >
                <span className="text-sm font-bold text-gray-900 flex-1">
                  {faq.q}
                </span>
                <ChevronDown
                  size={18}
                  className={`text-gray-400 transition-transform shrink-0 mt-0.5 ${
                    open ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {open && (
                <div className="px-4 pb-4 -mt-1">
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Still need help */}
      <div className="flex items-start gap-2 p-4 bg-[#F4EEFF] rounded-2xl">
        <LifeBuoy size={16} className="text-[#AF8BD4] shrink-0 mt-0.5" />
        <div className="text-xs text-gray-600 leading-relaxed">
          <p className="font-bold text-gray-900 mb-0.5">Still need help?</p>
          <p>
            Email us at{' '}
            <a
              href={`mailto:support@skytone.app?subject=${contactSubject}&body=${contactBody}`}
              className="font-bold text-[#AF8BD4] underline"
            >
              support@skytone.app
            </a>
            {' '}and we'll get back within 24 hours.
          </p>
        </div>
      </div>
    </SettingsLayout>
  );
};

export default HelpCenterPage;